/**
 * NearbyExperts — right-rail widget on the Lead Detail page.
 *
 * Queries `/api/search?q=<lead.category>` and surfaces up to 5 team members
 * most active in that category (visible to the caller). One click opens a
 * DM with the "Connecting via <Category>" toast so the other person knows
 * the context.
 *
 * Design tenets:
 * - Only render when the lead has a category set.
 * - Hide if no experts found (nothing worse than a "0 results" widget).
 * - Loading state uses skeleton shimmers, not a spinner.
 * - Never render the current user in their own results.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, MessageCircle, Sparkles, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import api from '@/lib/api';
import { MemberAvatar } from '@/components/MemberAvatar';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logger';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const MAX_EXPERTS = 5;

function ExpertSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-3 py-2 animate-pulse">
          <div className="w-8 h-8 rounded-sm bg-slate-100" />
          <div className="flex-1 space-y-1.5">
            <div className="h-2.5 bg-slate-100 rounded w-3/5" />
            <div className="h-2 bg-slate-50 rounded w-4/5" />
          </div>
        </div>
      ))}
    </>
  );
}

export default function NearbyExperts({ category, leadName, leadId }) {
  const [experts, setExperts] = useState(null); // null = loading, [] = empty
  const [meta, setMeta] = useState({ resolved: null, memberCount: 0 });
  const [handoffTarget, setHandoffTarget] = useState(null);
  const [handoffNote, setHandoffNote] = useState('');
  const [sendingHandoff, setSendingHandoff] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!category) { setExperts([]); return; }
    let cancelled = false;
    (async () => {
      try {
        setExperts(null);
        const res = await api.search(category, MAX_EXPERTS);
        if (cancelled) return;
        const data = res.data || {};
        // Prefer the exact-match roster (members_in_category) — those are
        // people with actual leads in this category. Fall back to the
        // preview_members from the categories list if no exact match.
        let candidates = data.members_in_category || [];
        let resolved = data.matched_category;
        if (!candidates.length) {
          const cat = (data.categories || []).find(
            (c) => c.name.toLowerCase() === category.toLowerCase(),
          ) || (data.categories || [])[0];
          candidates = cat?.preview_members || [];
          resolved = cat?.name || resolved;
        }
        // Filter out self
        const meId = user?.id || user?._id;
        const filtered = candidates.filter((m) => m.id !== meId).slice(0, MAX_EXPERTS);
        setExperts(filtered);
        setMeta({ resolved: resolved || category, memberCount: candidates.length });
      } catch (err) {
        logger.error(err);
        if (!cancelled) setExperts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [category, user]);

  // Don't render anything when there's no category on the lead or when
  // we've confirmed there are no experts to show — dead widgets are worse
  // than absent ones.
  if (!category) return null;
  if (experts && experts.length === 0) return null;

  const openDM = (expert) => {
    toast.message(`Connecting via ${meta.resolved}`, {
      description: leadName ? `Re: ${leadName}` : `${expert.name || expert.email}`,
    });
    navigate(`/messages?dm=${encodeURIComponent(expert.id)}`);
  };

  const openHandoff = (expert) => {
    setHandoffTarget(expert);
    setHandoffNote('');
  };

  const submitHandoff = async () => {
    if (!handoffTarget || !leadId) return;
    setSendingHandoff(true);
    try {
      const res = await api.leadHandoff(leadId, handoffTarget.id, handoffNote);
      const already = res.data?.already_shared;
      toast.success(
        already
          ? `Nudged ${handoffTarget.name || handoffTarget.email}`
          : `Handed off to ${handoffTarget.name || handoffTarget.email}`,
        {
          description: already
            ? 'They already had access — sent them a fresh DM about it.'
            : `They now have read access to ${leadName || 'this lead'} and got a DM asking for help.`,
        },
      );
      setHandoffTarget(null);
      navigate(`/messages?dm=${encodeURIComponent(handoffTarget.id)}`);
    } catch (err) {
      logger.error(err);
      toast.error(err?.response?.data?.detail || 'Could not send handoff');
    } finally {
      setSendingHandoff(false);
    }
  };

  return (
    <div
      className="border border-slate-200 rounded-sm bg-white p-4 sm:p-5"
      data-testid="nearby-experts-card"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-[#0047AB]" /> Nearby experts
          </p>
          <p className="text-sm font-heading font-semibold text-slate-900 mt-1 truncate">
            Active in <span className="text-[#0047AB]">{meta.resolved || category}</span>
          </p>
        </div>
        {experts && experts.length > 0 && (
          <span
            className="text-[10px] uppercase tracking-[0.15em] font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-sm px-1.5 py-0.5 flex-shrink-0"
            data-testid="nearby-experts-count"
          >
            {experts.length}
          </span>
        )}
      </div>

      {experts === null ? (
        <ExpertSkeleton />
      ) : (
        <div className="space-y-0.5" data-testid="nearby-experts-list">
          {experts.map((e) => (
            <div
              key={e.id}
              className="flex items-center gap-2 py-2 px-1 -mx-1 rounded-sm hover:bg-slate-50 transition-colors group"
              data-testid={`nearby-expert-${e.id}`}
            >
              <button
                onClick={() => openDM(e)}
                className="flex items-center gap-3 flex-1 min-w-0 text-left"
                data-testid={`nearby-expert-dm-${e.id}`}
              >
                <MemberAvatar userId={e.id} name={e.name} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900 font-medium truncate">
                    {e.name || <span className="text-slate-400 italic">Unnamed</span>}
                  </p>
                  <p className="text-[11px] text-slate-500 truncate">
                    {e.company || e.email}
                  </p>
                </div>
              </button>
              <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => openDM(e)}
                  title="DM only"
                  aria-label={`DM ${e.name || 'expert'}`}
                  className="p-1.5 rounded-sm hover:bg-white hover:text-[#0047AB] text-slate-500 transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                </button>
                {leadId && (
                  <button
                    onClick={() => openHandoff(e)}
                    title="Hand off — share lead + DM"
                    aria-label={`Hand off to ${e.name || 'expert'}`}
                    className="p-1.5 rounded-sm hover:bg-[#0047AB] hover:text-white text-slate-500 transition-colors"
                    data-testid={`nearby-expert-handoff-${e.id}`}
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {experts && experts.length > 0 && (
        <p className="text-[10px] text-slate-400 mt-3 pt-3 border-t border-slate-100 flex items-center gap-1.5">
          <Users className="w-2.5 h-2.5" />
          {leadId ? (
            <>Hover a row: <MessageCircle className="w-2.5 h-2.5 inline" /> DM only · <Send className="w-2.5 h-2.5 inline" /> Hand-off (share + DM)</>
          ) : (
            <>Click anyone to DM them about this lead.</>
          )}
        </p>
      )}

      {/* Handoff confirm dialog */}
      <Dialog open={!!handoffTarget} onOpenChange={(o) => !o && setHandoffTarget(null)}>
        <DialogContent className="max-w-md border-slate-200 rounded-sm" data-testid="handoff-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-slate-900">
              Hand off to {handoffTarget?.name || 'expert'}?
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-600">
              They&apos;ll get read access to <span className="font-semibold text-slate-900">{leadName || 'this lead'}</span> and a DM asking for help
              {meta.resolved ? <> · <span className="text-[#0047AB]">{meta.resolved}</span></> : null}.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium">
              Add a note (optional)
            </label>
            <Textarea
              value={handoffNote}
              onChange={(e) => setHandoffNote(e.target.value.slice(0, 400))}
              placeholder="e.g. Can you jump on this before Friday?"
              rows={3}
              className="mt-1.5 text-sm border-slate-200 focus:border-[#0047AB]"
              data-testid="handoff-note-input"
            />
            <p className="text-[10px] text-slate-400 mt-1 text-right">{handoffNote.length}/400</p>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="ghost"
              onClick={() => setHandoffTarget(null)}
              disabled={sendingHandoff}
              className="h-9"
              data-testid="handoff-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitHandoff}
              disabled={sendingHandoff}
              className="h-9 bg-[#0047AB] hover:bg-[#003580] text-white rounded-sm"
              data-testid="handoff-submit-btn"
            >
              {sendingHandoff ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
              Hand off &amp; open DM
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
